import React, { useState } from "react";
import { fmtSignedMoney, fmtPct } from "../investigations/format";
import { humanEUR } from "../cost-control/forecast/format";

const SORT_MOVER = "mover";
const SORT_CONCENTRATION = "concentration";

// Shared grid template so the column header and every row line up.
const COLS = "grid-cols-[104px_1fr_150px_104px_18px]";

function directionWord(d) {
  return d < 0 ? "decrease" : d > 0 ? "increase" : "movement";
}

// buildTakeaway — a one-sentence analytical summary above the list: the strongest single
// driver and its share, plus whether the SAME movement recurs across several lenses (which
// implies one underlying cause rather than independent changes — it's why the rows repeat).
export function buildTakeaway(rows, totalDelta) {
  if (!rows || !rows.length) return null;
  const lead = rows.reduce(
    (a, b) => (Math.abs(Number(b.topMover?.deltaCost) || 0) > Math.abs(Number(a.topMover?.deltaCost) || 0) ? b : a),
    rows[0]
  );
  const share = Math.round(Math.abs(Number(lead.topMover?.sharePct) || 0));
  const leadDelta = Math.round(Number(lead.topMover?.deltaCost) || 0);
  const dir = directionWord(totalDelta);
  const siblings = rows.filter(
    (r) => r !== lead
      && Math.round(Math.abs(Number(r.topMover?.sharePct) || 0)) === share
      && Math.round(Number(r.topMover?.deltaCost) || 0) === leadDelta
  );
  const label = lead.topMover?.label || lead.dimensionLabel;
  let s = `${label} explains ${share}% of the ${dir}.`;
  if (siblings.length) {
    const dims = [lead.dimensionLabel, ...siblings.map((r) => r.dimensionLabel)].map((x) => String(x).toLowerCase());
    const list = dims.length > 1 ? `${dims.slice(0, -1).join(", ")} and ${dims[dims.length - 1]}` : dims[0];
    s += ` The same movement appears across ${list}, suggesting one underlying driver rather than several independent changes.`;
  }
  return s;
}

// DimensionAttributionPanel — "what explains the change, seen through each lens". A compact
// diagnostic list: one row per candidate dimension, showing its single biggest driver, that
// driver's contribution bar + share of the total change, the € change (neutral — this is
// analysis, not a breach), and the next-largest driver inline. Clicking a row breaks the
// chart+table down by that dimension. Sort toggles between the largest single change
// (default) and how concentrated the change is within each lens (ordering only).
export default function DimensionAttributionPanel({ dimensions = [], totalDelta = 0, onPickDimension }) {
  const [sortBy, setSortBy] = useState(SORT_MOVER);

  const rows = sortBy === SORT_CONCENTRATION
    ? [...dimensions].sort((a, b) => (Number(b.concentration) || 0) - (Number(a.concentration) || 0))
    : dimensions;

  const dir = directionWord(totalDelta);
  const takeaway = buildTakeaway(dimensions, totalDelta);

  return (
    <div className="rounded-card border border-card-bd bg-card p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[16px] font-semibold leading-[22px] text-text">
            What explains the {humanEUR(Math.abs(totalDelta))} {dir}?
          </div>
          <div className="mt-1 text-[12px] text-text-3">
            Top contributor in each lens · select a row to break the chart down by that dimension.
          </div>
          <div className="mt-0.5 text-[12px] tabular-nums text-text-3">
            Total change: {fmtSignedMoney(totalDelta)} vs previous period
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1 rounded-seg bg-side p-1 text-[12px]">
          {[
            [SORT_MOVER, "Largest change", null],
            [SORT_CONCENTRATION, "Concentration", "Shows whether the change is driven by one item or spread across many items."],
          ].map(([key, label, tip]) => (
            <button
              key={key}
              type="button"
              title={tip || undefined}
              onClick={() => setSortBy(key)}
              aria-pressed={sortBy === key}
              className={`box-border rounded-tab border border-solid border-transparent px-3 py-1 transition-colors ${
                sortBy === key ? "bg-primary-soft-strong font-semibold text-primary shadow-pill" : "text-text-3 hover:text-text-2"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-text-3">No dimension with a material change.</div>
      ) : (
        <>
          {takeaway && (
            <div className="rounded-md bg-surface px-3 py-2 text-[13px] leading-[19px] text-text-2">{takeaway}</div>
          )}

          {/* Column header */}
          <div className={`grid ${COLS} items-center gap-3 px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-text-3 border-b border-solid border-card-bd`}>
            <div>Dimension</div>
            <div>Top driver</div>
            <div>Contribution</div>
            <div className="text-right">Change</div>
            <div />
          </div>

          {/* Rows — a divided list, not separate cards. */}
          <div className="-mt-2">
            {rows.map((item) => {
              const runner = item.runners && item.runners[0];
              // Share is signed (negative for a decrease); the bar and the label want the
              // magnitude — "55% of the decrease", not "−55%".
              const shareAbs = Math.abs(Number(item.topMover?.sharePct) || 0);
              const share = Math.max(0, Math.min(100, shareAbs));
              return (
                <button
                  key={item.dimension}
                  type="button"
                  data-testid="dimension-attribution-row"
                  onClick={() => onPickDimension?.(item.dimension)}
                  style={{ width: "100%" }}
                  className={`group grid ${COLS} items-center gap-3 box-border cursor-pointer border-0 border-b border-solid border-card-bd px-3 py-3 text-left transition-colors last:border-b-0 hover:bg-surface`}
                >
                  <span className="justify-self-start shrink-0 rounded-ctrl bg-surface-2 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-text-3">
                    {item.dimensionLabel}
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-[14px] font-semibold text-text">{item.topMover?.label}</div>
                    {runner && (
                      <div className="truncate text-[12px] text-text-3">
                        Next: {runner.label} {fmtSignedMoney(runner.deltaCost)}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Bar sized with inline height — the theme overrides the h-* scale, so
                        h-1.5/h-full collapse to 0 and the bar would be invisible. */}
                    <div className="flex-1 overflow-hidden rounded-full bg-surface-2" style={{ height: 6 }}>
                      <div className="rounded-full bg-primary" style={{ height: 6, width: `${share}%` }} />
                    </div>
                    <span className="shrink-0 tabular-nums text-[14px] font-medium text-text-2" style={{ width: 36, textAlign: "right" }}>
                      {fmtPct(shareAbs)}
                    </span>
                  </div>
                  <span className="justify-self-end tabular-nums text-[15px] font-semibold text-text">
                    {fmtSignedMoney(item.topMover?.deltaCost)}
                  </span>
                  <span className="justify-self-end text-[13px] text-text-3 opacity-0 transition-opacity group-hover:text-primary group-hover:opacity-100" aria-hidden="true">
                    ›
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
